import React from "react";
import { useTranslation } from "react-i18next";
import type { Filterset } from "@/app/services/filtersetService";
import { Modal, ModalSize } from "./Modal";
import { SearchableSelect, Option } from "./SearchableSelect";
import { Button, ButtonSize, ButtonVariant } from "@/shared/ui/Button";
import { FiltersetNameField } from "./FiltersetNameField";
import { Input } from "./Input";
import Tooltip from "@/shared/components/Tooltip";

interface FiltersetEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  name: string;
  onNameChange: (value: string) => void;
  nameError?: string | undefined;
  targetModel?: string | undefined;
  excludeFiltersetId?: number;
  /** Original name when editing — suppresses the same-name duplicate check. */
  originalName?: string | undefined;
  onExactNameMatchChange?: (filterset: Filterset | null) => void;
  mappingLabel?: string;
  showEnabled?: boolean;
  enabled?: boolean;
  onEnabledChange?: (checked: boolean) => void;
  showShared?: boolean;
  shared?: boolean;
  onSharedChange?: (checked: boolean) => void;
  /** When true, renders the Shared checkbox in the second column beside the Name field instead of in the second row. */
  sharedInNameRow?: boolean;
  showUser?: boolean;
  userOptions?: Option[];
  selectedUserOption?: Option | null;
  onUserChange?: (selected: Option | Option[] | null) => void;
  isLoadingUsers?: boolean;
  userDisplayLabel?: string | undefined;
  expression: string;
  expressionError?: string | undefined;
  editableExpression?: boolean;
  onExpressionChange?: (value: string) => void;
  saveLabel: string;
  savingLabel: string;
  onSave: () => void;
  isSaving?: boolean;
  dataTestId: string;
}

export const FiltersetEditorModal: React.FC<FiltersetEditorModalProps> = ({
  isOpen,
  onClose,
  title,
  name,
  onNameChange,
  nameError,
  targetModel,
  excludeFiltersetId,
  originalName,
  onExactNameMatchChange,
  mappingLabel,
  showEnabled = false,
  enabled = false,
  onEnabledChange,
  showShared = false,
  shared = false,
  onSharedChange,
  sharedInNameRow = false,
  showUser = false,
  userOptions = [],
  selectedUserOption = null,
  onUserChange,
  isLoadingUsers = false,
  userDisplayLabel,
  expression,
  expressionError,
  editableExpression = false,
  onExpressionChange,
  saveLabel,
  savingLabel,
  onSave,
  isSaving = false,
  dataTestId,
}) => {
  const { t } = useTranslation();
  // When sharedInNameRow is true, Shared is rendered beside Name; exclude it from the second row.
  const showSharedInSecondRow = showShared && !sharedInNameRow;
  const showSecondRow = showUser || userDisplayLabel !== undefined || showEnabled || showSharedInSecondRow;

  return (
  <Modal
      isOpen={isOpen}
   onClose={onClose}
      title={title}
   size={ModalSize.Xl}
   data-testid={dataTestId}
   preventCloseOnBackdrop={true}
    >
      <div className="space-y-4">
    <div className="text-2xl font-medium text-text">{t('common.general', 'General')}</div>

      <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <div>
              <Tooltip text={t('common.name', 'Name')} position="top" align="left">
                <label htmlFor="filterset-name" className="block text-sm font-medium text-text mb-1">
                  {t('common.name', 'Name')} <span className="text-error">*</span>
                </label>
              </Tooltip>
         <FiltersetNameField
                id="filterset-name"
      value={name}
onChange={onNameChange}
    placeholder="Enter filter set name"
      dataTestId={`${dataTestId}-name-input`}
                error={nameError}
     targetModel={targetModel}
    excludeFiltersetId={excludeFiltersetId}
      originalName={originalName}
 autoFocusOnMount={isOpen}
                onExactMatchChange={onExactNameMatchChange}
           />
        </div>

       {mappingLabel !== undefined ? (
     <div>
         <Tooltip text={t('shared.model', 'Model')} position="top" align="left">
           <label
             htmlFor="filterset-model"
             className="block text-sm font-medium text-text mb-1"
           >
             {t('shared.model', 'Model')}
           </label>
         </Tooltip>
            <input
       id="filterset-model"
       type="text"
          value={mappingLabel}
       readOnly
    disabled
    className="block w-full h-[36px] border border-border rounded-[4px] py-[6px] px-[10px] text-[14px] text-text bg-surface-alt disabled:cursor-not-allowed disabled:opacity-70"
   data-testid={`${dataTestId}-mapping-input`}
     />
           </div>
) : sharedInNameRow && showShared ? (
      <div className="flex items-center md:pb-2">
      <label className="inline-flex items-center gap-2 text-sm text-text">
      <input
         data-testid={`${dataTestId}-shared-checkbox`}
        type="checkbox"
 checked={shared}
 onChange={(event) => onSharedChange?.(event.target.checked)}
      className="h-4 w-4"
        />
     {t('shared.shared', 'Shared')}
       </label>
            </div>
            ) : null}
          </div>

          {showSecondRow && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            {showUser ? (
     <div>
       <label
  htmlFor="filterset-user"
           className="block text-sm font-medium text-text mb-1"
  >
     {t('history.user', 'User')}
       </label>
        <SearchableSelect
           id="filterset-user"
         data-testid={`${dataTestId}-user-select`}
    options={userOptions}
value={selectedUserOption}
         onChange={(selected) => onUserChange?.(selected)}
          placeholder={isLoadingUsers ? "Loading users..." : "Select user"}
                    disabled={isLoadingUsers}
        multiple={false}
   showCheckbox={false}
         showClearButton={false}
       />
        </div>
  ) : userDisplayLabel !== undefined ? (
       <div>
     <label
               htmlFor="filterset-user-display"
      className="block text-sm font-medium text-text mb-1"
          >
 {t('history.user', 'User')}
          </label>
        <Input
       id="filterset-user-display"
  type="text"
              value={userDisplayLabel}
              readOnly
   disabled
          data-testid={`${dataTestId}-user-display`}
           />
    </div>
              ) : (
      <div />
      )}

      <div className="flex flex-wrap items-center gap-6 min-h-10 md:pb-2">
        {showEnabled && (
               <label className="inline-flex items-center gap-2 text-sm text-text">
    <input
  data-testid={`${dataTestId}-enabled-checkbox`}
      type="checkbox"
        checked={enabled}
    onChange={(event) => onEnabledChange?.(event.target.checked)}
             className="h-4 w-4"
           />
     {t('common.enabled', 'Enabled')}
         </label>
  )}
                {showSharedInSecondRow && (
        <label className="inline-flex items-center gap-2 text-sm text-text">
   <input
data-testid={`${dataTestId}-shared-checkbox`}
         type="checkbox"
            checked={shared}
          onChange={(event) => onSharedChange?.(event.target.checked)}
      className="h-4 w-4"
     />
   {t('shared.shared', 'Shared')}
   </label>
      )}
 </div>
       </div>
          )}
    </div>

      <div>
        <Tooltip text={t('shared.filterExpression', 'Filter expression')} position="top" align="left">
          <label
            htmlFor="filterset-expression"
            className="block text-sm font-medium text-text mb-1"
          >
            {t('shared.filterExpression', 'Filter expression')}{ editableExpression && <span className="text-error">*</span>}
          </label>
        </Tooltip>
          {editableExpression ? (
  <textarea
              id="filterset-expression"
        value={expression}
      onChange={(event) => onExpressionChange?.(event.target.value)}
      className={`w-full border rounded-[4px] py-[6px] px-[10px] text-[14px] text-text bg-surface focus:outline-none focus:ring-2 focus:ring-primary ${
        expressionError ? "border-error" : "border-border"
        }`}
     rows={2}
      data-testid={`${dataTestId}-expression-input`}
            />
   ) : (
            <div
      className={`bg-surface-secondary p-3 rounded border text-xs text-text-muted break-all ${expressionError ? "border-error" : "border-border"}`}
    data-testid={`${dataTestId}-expression-input`}
 >
              {expression}
   </div>
      )}
          {expressionError && <p role="alert" className="text-error text-xs mt-1">{expressionError}</p>}
    </div>

        <div className="pt-3 border-t border-border flex items-center gap-2 justify-end">
        <Button
           data-testid={`${dataTestId}-cancel-button`}
       variant={ButtonVariant.Ghost}
   size={ButtonSize.Sm}
              onClick={onClose}
              disabled={isSaving}
            >
           {t('common.cancel', 'Cancel')}
            </Button>
    <Button
   data-testid={`${dataTestId}-save-button`}
         variant={ButtonVariant.Primary}
            size={ButtonSize.Sm}
 onClick={onSave}
            disabled={isSaving}
          >
    {isSaving ? savingLabel : saveLabel}
          </Button>
      </div>
      </div>
    </Modal>
  );
};
